import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAssignDevice, type Device } from '@/hooks'
import { useActiveEmployees } from '@/hooks'

interface AssignDialogProps {
  device: Device
  onClose: () => void
  onSuccess: () => void
}

export function AssignDialog({ device, onClose, onSuccess }: AssignDialogProps) {
  const [employeeId, setEmployeeId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const { data: employees, isLoading: employeesLoading } = useActiveEmployees()
  const assignMutation = useAssignDevice()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!employeeId) {
      setError('请选择员工')
      return
    }

    const submitAsync = async () => {
      try {
        await assignMutation.mutateAsync({
          id: device.id,
          data: {
            employeeId,
            notes: notes || undefined,
          },
        })
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : '分配失败')
      }
    }

    void submitAsync()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">分配设备</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 rounded-md bg-muted p-3">
          <p className="text-sm text-muted-foreground">设备: {device.name}</p>
          <p className="text-sm text-muted-foreground">序列号: {device.serialNumber}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">选择员工 *</label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder={employeesLoading ? '加载中...' : '选择员工'} />
              </SelectTrigger>
              <SelectContent>
                {employees?.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} ({emp.employeeNo})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">备注</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入分配备注"
              rows={3}
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={assignMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {assignMutation.isPending ? '分配中...' : '确认分配'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
